import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import socket from '../services/socket';

const AuthContext = createContext();

const normalizeUser = (user) => {
  if (!user) return user;
  if (user.avatar && typeof user.avatar === 'string') {
    // Strip old localhost base if it was saved previously
    if (user.avatar.startsWith('http://localhost:5000')) {
      return { ...user, avatar: user.avatar.replace('http://localhost:5000', '') };
    }
  }
  return user;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem('user');
    return u ? normalizeUser(JSON.parse(u)) : null;
  });
  const [loading, setLoading] = useState(true);

  const joinUserRoom = (userId) => {
    if (socket.connected) socket.emit('joinUserRoom', userId);
    else socket.once('connect', () => socket.emit('joinUserRoom', userId));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me')
        .then(({ data }) => {
          const normalized = normalizeUser(data.user);
          setUser(normalized);
          localStorage.setItem('user', JSON.stringify(normalized));
          socket.connect();
          joinUserRoom(data.user._id);
        })
        .catch(() => { localStorage.removeItem('token'); localStorage.removeItem('user'); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Ensure presence is re-joined on socket reconnects while logged in
  useEffect(() => {
    if (!user) return;
    const handleConnect = () => {
      if (user?._id) socket.emit('joinUserRoom', user._id);
    };
    // server:ready = server just restarted and DB is ready; re-announce presence
    const handleServerReady = () => {
      if (user?._id) socket.emit('joinUserRoom', user._id);
    };
    socket.on('connect', handleConnect);
    socket.on('server:ready', handleServerReady);
    return () => {
      socket.off('connect', handleConnect);
      socket.off('server:ready', handleServerReady);
    };
  }, [user]);

  // Heartbeat: tell the server we are still alive every 30 s
  useEffect(() => {
    if (!user?._id) return;
    const id = setInterval(() => {
      if (socket.connected) socket.emit('heartbeat', user._id);
    }, 30_000);
    return () => clearInterval(id);
  }, [user?._id]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    const normalized = normalizeUser(data.user);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(normalized));
    setUser(normalized);
    socket.connect();
    joinUserRoom(data.user._id);
    return data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post('/auth/register', { name, email, password });
    const normalized = normalizeUser(data.user);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(normalized));
    setUser(normalized);
    socket.connect();
    joinUserRoom(data.user._id);
    return data;
  }, []);

  const logout = useCallback(async () => {
    if (user) socket.emit('leaveUserRoom', user._id);
    await api.post('/auth/logout');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    socket.disconnect();
  }, [user]);

  const role = user?.role === 'user' ? 'staff' : user?.role;
  const isAdmin = role === 'super_admin' || role === 'admin' || role === 'accounts' || role === 'hr';
  const isSuperAdmin = role === 'super_admin';
  const isStaff = role === 'staff';
  const isAccountsOrHr = role === 'accounts' || role === 'hr';

  return (
    <AuthContext.Provider value={{
      user: user ? { ...user, role: role || user.role } : null,
      loading,
      login,
      register,
      logout,
      isAdmin,
      isSuperAdmin,
      isStaff,
      isAccountsOrHr,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
